import { api } from './api';

const tokenPublicStaging = 'Basic NjM1YWUwNmY4Y2Y1OGU0MTFlZWIwYjhlOjYzNGQyN2QwOTI3MWYzMzdiMzlmMTQ1OQ==';
const tokenPublicProd = 'Basic NjMyYjU2YzE1NDVkZDA3Njc2NjIxNzA5OjYzNGQyN2QwOTI3MWYzMzdiMzlmMTQ1OQ==';

export type TriggerImageStatus = 'success' | 'error' | 'failed' | 'processing' | string;

export type TriggerImageResult = {
  status: TriggerImageStatus;
  _id?: string;
  createdAt?: number | { $date?: number };
  userPhoto?: {
    mediaType?: string;
    data?: string;
    [key: string]: unknown;
  };
  imageBase64?: string;
  imageUrl?: string;
  saved?: boolean;
  promptStyle?: string;
  player?: string;
  provider?: string;
  model?: string;
  size?: string;
  error?: string;
  [key: string]: unknown;
};

function getBasicAuthHeader() {
  const href = typeof window !== 'undefined' ? window.location.href : '';
  const isStaging = !!href.match(/(funifier\.app|localhost)/gi);
  return isStaging ? tokenPublicStaging : tokenPublicProd;
}

export function ensureDataUri(imageData: string, mimeType = 'image/png') {
  if (imageData.startsWith('data:')) {
    return imageData;
  }

  return `data:${mimeType};base64,${imageData}`;
}

export function parseDataUri(dataUri: string) {
  const matches = dataUri.match(/^data:([^;]+);base64,([\s\S]+)$/);

  if (!matches) {
    throw new Error('Invalid data URI format');
  }

  return {
    mimeType: matches[1],
    base64Data: matches[2],
  };
}

/**
 * Convert a base64 data URI to a Blob
 * @param imageData - Base64 string or full data URI (e.g., "data:image/png;base64,...")
 * @returns Blob object
 */
export function base64ToBlob(imageData: string): Blob {
  if (imageData.startsWith('http://') || imageData.startsWith('https://')) {
    throw new Error('Expected base64 data URI, got URL');
  }

  const dataUri = ensureDataUri(imageData);

  const { mimeType, base64Data } = parseDataUri(dataUri);

  try {
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  } catch (decodeError) {
    throw new Error('Failed to decode base64 image data');
  }
}

/**
 * Generate image via Funifier trigger
 * POST v3/database/generate_image__c (Basic auth)
 * Frame is NOT sent to AI - composited after generation in Processing.tsx
 * @param promptStyle - Style prompt for generation
 * @param provider - "openai" or "gemini"
 * @param userPhotoData - Base64 data of user photo (without data URI prefix)
 * @param userPhotoMediaType - Media type (e.g., "image/png", "image/jpeg")
 * @returns Trigger response with imageBase64, model, size, status
 */
export function deriveProvider(modelName: string): 'openai' | 'gemini' {
  return modelName.toLowerCase().includes('gemini') ? 'gemini' : 'openai';
}

export async function generateImageViaTrigger(
  promptStyle: string,
  provider: 'openai' | 'gemini',
  userPhotoData: string,
  userPhotoMediaType: string,
  userId?: string,
  styleName?: string,
): Promise<TriggerImageResult> {
  const body: Record<string, unknown> = {
    promptStyle,
    provider,
    userPhoto: {
      mediaType: userPhotoMediaType,
      data: userPhotoData,
    },
  };

  if (userId) {
    body.player = userId;
  }

  if (styleName) {
    body.style = styleName;
  }

  try {
    const { data } = await api.post('database/generate_image__c', body, {
      headers: {
        Authorization: getBasicAuthHeader(),
      },
    });

    const result: TriggerImageResult = data;

    if (result.status !== 'success') {
      const errorMsg = result.error || 'Erro desconhecido na geração';
      throw new Error(errorMsg);
    }

    return result;
  } catch (error) {
    throw error;
  }
}

function resolveCreatedAtDateValue(createdAt?: number | { $date?: number }) {
  if (typeof createdAt === 'number') {
    return createdAt;
  }

  if (createdAt && typeof createdAt === 'object' && typeof createdAt.$date === 'number') {
    return createdAt.$date;
  }

  return Date.now();
}

export async function saveGeneratedImageViaTrigger(result: TriggerImageResult): Promise<TriggerImageResult> {
  const payload: TriggerImageResult = {
    ...result,
    saved: true,
    createdAt: {
      $date: resolveCreatedAtDateValue(result.createdAt),
    },
  };

  const { data } = await api.put('database/generate_image__c', payload, {
    headers: {
      Authorization: getBasicAuthHeader(),
    },
  });

  return data as TriggerImageResult;
}
